import React, { useContext, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// import Highcharts from 'highcharts/es-modules/masters/highcharts.src.js';
// import HighchartsReact from 'highcharts-react-official';
import {
  CALL_OPTION,
  OPTION_CONTRACTS_TYPES,
  PUT_OPTION,
  selectors,
} from 'state';
import { formatters } from 'services';
import { TraineeSessionContext } from '../TraineeSessionContext';
import { TraineeOptionsContext } from '../TraineeOptionsContext';
import { OptionsContractType } from '../../../state/app/options/types';

interface OptionsChainTableProps {
  asset_id: number;
  expiration: string;
}

interface OptionsChainRow {
  strike: number;
  call?: OptionsContractType;
  put?: OptionsContractType;
}

const TermStructure: React.FC<OptionsChainTableProps> = ({
  asset_id,
  expiration,
}) => {
  const dispatch = useDispatch();
  const optionsContracts: OptionsContractType[] =
    useSelector(selectors.assetOptionsContracts(asset_id)) || [];
  const underlyingSpot = useSelector(selectors.underlyingSpot(asset_id));
  const { scenario_date } = useSelector(selectors.activeSession);
  const optionsContext = useContext(TraineeOptionsContext);
  const selectedOptionsContract = useSelector(
    selectors.getSelectedOptionsContract
  );

  const chosenStrike = selectedOptionsContract?.strike;
  const chosenType = optionsContext.selectedOptionType; // CALL_OPTION or PUT_OPTION

  const termStructureData = useMemo<
    { term: number; impliedVol: number }[]
  >(() => {
    if (chosenStrike == null || chosenType == null) {
      return [];
    }
    const days = (ms: number) => ms / (1000 * 60 * 60 * 24);
    // gather all expirations present
    const expirations = Array.from(
      new Set(optionsContracts.map(c => c.expiration))
    ).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    const data: { term: number; impliedVol: number }[] = [];
    expirations.forEach(expDate => {
      // TODO: this data filter (similar to OptionsChains.tsx data filter) should be
      //  externalized to a common hook
      // reuse the same per-expiration grouping/filtering from chainRows
      const slice = optionsContracts.filter(c => c.expiration === expDate);
      // determine tickSize once per expiration (could extract if needed)
      const tick = (() => {
        const spot = underlyingSpot ?? 0;
        if (spot <= 10) return 1;
        if (spot <= 25) return 5;
        if (spot <= 100) return 10;
        if (spot <= 250) return 25;
        if (spot <= 500) return 50;
        return 100;
      })();
      // group by strike and pick only rows with at least one IV
      const byStrike: Record<
        number,
        {
          strike: number;
          call?: OptionsContractType;
          put?: OptionsContractType;
        }
      > = {};
      slice.forEach(opt => {
        if (!Number.isInteger(opt.strike / tick)) return;
        if (!byStrike[opt.strike])
          byStrike[opt.strike] = { strike: opt.strike };
        if (opt.type_int === 0) byStrike[opt.strike].call = { ...opt };
        else byStrike[opt.strike].put = { ...opt };
      });
      const rows = Object.values(byStrike).filter(
        r =>
          r.call?.implied_volatility != null ||
          r.put?.implied_volatility != null
      );
      rows.forEach(r => {
        // fill missing IV
        if (r.call?.implied_volatility == null && r.put)
          r.call!.implied_volatility = r.put.implied_volatility!;
        if (r.put?.implied_volatility == null && r.call)
          r.put!.implied_volatility = r.call.implied_volatility!;
      });
      // find the row for chosenStrike
      const match = rows.find(r => r.strike === chosenStrike);
      if (match) {
        const c = chosenType === CALL_OPTION ? match.call! : match.put!;
        data.push({
          term: days(
            new Date(expDate).getTime() - new Date(scenario_date).getTime()
          ),
          impliedVol: c.implied_volatility,
        });
      }
    });
    return data.sort((a, b) => a.term - b.term);
  }, [
    optionsContracts,
    chosenStrike,
    chosenType,
    scenario_date,
    underlyingSpot,
  ]);

  // Compute min/max across all option contracts for fixed y-axis
  // const allVols = optionsContracts.map(c => c.implied_volatility);
  // const yMin = Math.max(0, Math.min(...allVols) - 0.05);
  // const yMax = Math.max(...allVols) + 0.05;

  // 3. Configure Highcharts
  // const chartOptions: Highcharts.Options = {
  //   chart: {
  //     type: 'line',
  //     // zooming: {
  //     //   type: 'x',
  //     // },
  //     // height: '400px',
  //     // width: '900px',
  //   },
  //
  //   title: {
  //     text: `${chosenStrike} ${OPTION_CONTRACTS_TYPES[chosenType]} Term Structure`,
  //   },
  //   xAxis: {
  //     // categories: termLabels,
  //     // tickInterval: 1,
  //     tickPositions: termStructureData.map(d => d.term),
  //     labels: {
  //       formatter: function () {
  //         return `${this.value}`;
  //       },
  //     },
  //     title: { text: 'Expiry, days' },
  //   },
  //   yAxis: {
  //     // min: yMin,
  //     // max: yMax,
  //     title: { text: 'Implied Volatility' },
  //     labels: {
  //       // Example: display "30%" if impliedVol=30
  //       formatter: function () {
  //         // return `${(this.value as number).toFixed(2)}%`;
  //         return `${formatters.percent0Digits(this.value)}`;
  //       },
  //     },
  //   },
  //   credits: { enabled: false },
  //   legend: { enabled: false },
  //   tooltip: {
  //     formatter: function () {
  //       return `${this.x}-Day<br/>${formatters.percent0Digits(this.y)}`;
  //     },
  //   },
  //   // plotOptions: {
  //   //   column: {
  //   //     dataLabels: {
  //   //       enabled: true,
  //   //       formatter: function () {
  //   //         return formatters.percent0Digits(this.y);
  //   //       },
  //   //     },
  //   //   },
  //   // },
  //
  //   // 4. Wrap smileData inside a single series object
  //   series: [
  //     {
  //       name: 'IV',
  //       type: 'line',
  //       data: termStructureData.map(d => [d.term, d.impliedVol]),
  //       // optional styling
  //       // marker: { enabled: true },
  //     },
  //   ],
  // };

  // return (
  //   <HighchartsReact
  //     highcharts={Highcharts}
  //     options={chartOptions}
  //     // style={{ width: '600px' }}
  //   />
  // );
  return (
    <div>
      Highcharts removed, this part of the code was not yet rewrote to use
      ApexChart
    </div>
  );
};

export default TermStructure;
